import React, { useState, useEffect } from 'react';
import FeedbackNotification from '../common/FeedbackNotification';
import { X, Image } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText, sanitizeHtml, sanitizeUrl, sanitizeObject } from '../../security/sanitize';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';

const PortfolioForm = ({ item, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    link: '',
    featured: false,
    coverImage: '',
    images: [],
  });
  const [imageFile, setImageFile] = useState(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState([]);
  const [imagePreview, setImagePreview] = useState('');
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('success');

  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        category: item.category || '',
        description: item.description || '',
        link: item.link || '',
        featured: item.featured || false,
        coverImage: item.coverImage || '',
        images: item.images || [],
      });
      
      if (item.coverImage) {
        setImagePreview(item.coverImage);
      }
      
      if (item.images && item.images.length > 0) {
        setAdditionalImagePreviews(item.images);
      }
    }
  }, [item]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAdditionalImageFiles(prevFiles => [...prevFiles, ...files]);
      
      // Create previews for each file
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setAdditionalImagePreviews(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImagePreviews(prev => prev.filter((_, i) => i !== index));
    setAdditionalImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCategoryChange = (value) => {
    setFormData(prev => ({ ...prev, category: value }));
    if (errors.category) {
      setErrors(prev => ({ ...prev, category: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (formData.link) {
      // Accept links with or without protocol — prepend https:// if missing
      const linkToTest = formData.link.startsWith('http') ? formData.link : `https://${formData.link}`;
      try {
        new URL(linkToTest);
      } catch {
        newErrors.link = 'Please enter a valid URL';
      }
    }
    
    if (!imagePreview && !imageFile) {
      newErrors.coverImage = 'Cover image is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get CSRF token from form
    const form = new FormData(e.target);
    const csrfToken = form.get('csrfToken');
    
    // Validate CSRF token
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      setFeedbackType('error');
      setFeedbackMessage('Security validation failed. Please refresh the page and try again.');
      setShowFeedback(true);
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setFeedbackMessage('');
    
    try {
      // Sanitize user input
      const sanitizedData = {
        title: sanitizeText(formData.title),
        category: sanitizeText(formData.category),
        description: sanitizeHtml(formData.description),
        link: sanitizeUrl(formData.link),
        featured: formData.featured
      };
      
      // Prepare the data for submission to Firestore
      const data = {
        ...sanitizedData,
        id: item?.id || `portfolio-${Date.now()}`,
        // Keep the existing URLs if no new files are uploaded
        coverImage: item?.coverImage || '',
        images: item?.images || [],
        // Add the files for upload
        coverImageFile: imageFile,
        additionalImageFiles: additionalImageFiles
      };
      
      await onSubmit(data);
      
      // Show success feedback
      setFeedbackType('success');
      setFeedbackMessage('Portfolio item saved successfully!');
      setShowFeedback(true);
      
      // Clear feedback after animation completes
      setTimeout(() => {
        setShowFeedback(false);
      }, 3000);
    } catch (error) {
      // Show error feedback
      setFeedbackType('error');
      setFeedbackMessage('Failed to save portfolio item. Please try again.');
      setShowFeedback(true);
      
      // Clear feedback after animation completes
      setTimeout(() => {
        setShowFeedback(false);
      }, 3000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 relative">
      <CSRFToken />
      {/* Feedback Notification */}
      <FeedbackNotification
        show={showFeedback}
        onClose={() => setShowFeedback(false)}
        type={feedbackType}
        message={feedbackMessage}
        autoCloseTime={3000}
      />
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
          Title *
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm ${
            errors.title ? 'border-red-500' : ''
          }`}
        />
        {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
      </div>

      <div>
        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
          Category *
        </label>
        <Select
          value={formData.category}
          onValueChange={handleCategoryChange}
        >
          <SelectTrigger id="category" error={errors.category} className="w-full">
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Product Review">Product Review</SelectItem>
            <SelectItem value="Sponsored Post">Sponsored Post</SelectItem>
            <SelectItem value="Brand Collaboration">Brand Collaboration</SelectItem>
            <SelectItem value="Video Content">Video Content</SelectItem>
            <SelectItem value="Blog Post">Blog Post</SelectItem>
            <SelectItem value="Social Media Campaign">Social Media Campaign</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
        {errors.category && <p className="mt-1 text-sm text-red-500">{errors.category}</p>}
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description *
        </label>
        <textarea
          id="description"
          name="description"
          rows={4}
          value={formData.description}
          onChange={handleChange}
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm ${
            errors.description ? 'border-red-500' : ''
          }`}
        />
        {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
      </div>

      <div>
        <label htmlFor="link" className="block text-sm font-medium text-gray-700">
          Content Link
        </label>
        <input
          type="url"
          id="link"
          name="link"
          value={formData.link}
          onChange={handleChange}
          placeholder="https://example.com"
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm ${
            errors.link ? 'border-red-500' : ''
          }`}
        />
        {errors.link && <p className="mt-1 text-sm text-red-500">{errors.link}</p>}
        <p className="mt-1 text-xs text-gray-500">
          Add a link to your social media post, video, or blog review
        </p>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="featured"
          name="featured"
          checked={formData.featured}
          onChange={handleChange}
          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
        />
        <label htmlFor="featured" className="ml-2 block text-sm text-gray-700">
          Feature this item in your profile
        </label>
      </div>

      {/* Cover Image Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Cover Image *
        </label>
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
          <div className="space-y-1 text-center">
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Cover preview"
                  className="mx-auto h-32 w-auto object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setImagePreview('');
                    setImageFile(null);
                  }}
                  className="absolute top-0 right-0 bg-white rounded-full p-1 shadow-sm text-gray-500 hover:text-red-500"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <Image className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="cover-image-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none"
                  >
                    <span>Upload a cover image</span>
                    <input
                      id="cover-image-upload"
                      name="cover-image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleCoverImageChange}
                      className="sr-only"
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF up to 5MB
                </p>
              </>
            )}
          </div>
        </div>
        {errors.coverImage && <p className="mt-1 text-sm text-red-500">{errors.coverImage}</p>}
      </div>

      {/* Additional Images Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional Images
        </label>
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
          <div className="space-y-1 text-center">
            <Image className="mx-auto h-12 w-12 text-gray-400" />
            <div className="flex text-sm text-gray-600">
              <label
                htmlFor="additional-images-upload"
                className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none"
              >
                <span>Upload additional images</span>
                <input
                  id="additional-images-upload"
                  name="additional-images-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleAdditionalImagesChange}
                  className="sr-only"
                />
              </label>
            </div>
            <p className="text-xs text-gray-500">
              PNG, JPG, GIF up to 5MB each
            </p>
          </div>
        </div>

        {/* Additional Image Previews */}
        {additionalImagePreviews.length > 0 && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {additionalImagePreviews.map((preview, index) => (
              <div key={index} className="relative">
                <img
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  className="h-24 w-full object-cover rounded-md"
                />
                <button
                  type="button"
                  onClick={() => removeAdditionalImage(index)}
                  className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-sm text-gray-500 hover:text-red-500"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          {submitting ? (
            <>
              <LoadingSpinner size="xs" color="white" />
              Saving...
            </>
          ) : (
            'Save Portfolio Item'
          )}
        </button>
      </div>
    </form>
  );
};

export default PortfolioForm;
